import { LeftOutlined } from '@ant-design/icons'
import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form } from 'antd'
import { useEffect } from 'react'
import { useNavigate, useParams } from 'react-router-dom'

import {
  useCounterpartServiceCreateOtherInstitutionEndpoint,
  useCounterpartServiceGetOtherInstitutionEndpoint,
  useCounterpartServiceGetOtherInstitutionListEndpointKey,
  useCounterpartServiceUpdateOtherInstitutionEndpoint
} from 'src/api/queries'
import { ApiError, LegalEntityStatus } from 'src/api/requests'
import {
  FormFooter,
  LoadingScreen,
  message,
  PageBreadcrumbs
} from 'src/components'
import { useLocales } from 'src/locales'
import { paths } from 'src/paths'
import { formErrorHandler } from 'src/utils'

import { OtherInstitutionsDetails } from '../components'

export const OtherInstitutionsAddPage = () => {
  const { t, currentLang } = useLocales()
  const navigate = useNavigate()

  const { id } = useParams()
  const queryClient = useQueryClient()

  const [form] = Form.useForm()

  const onMutationSuccess = () => {
    if (id) {
      queryClient.invalidateQueries({
        queryKey: [
          useCounterpartServiceGetOtherInstitutionListEndpointKey,
          String(id)
        ]
      })
    } else {
      queryClient.invalidateQueries({
        queryKey: [useCounterpartServiceGetOtherInstitutionListEndpointKey]
      })
    }
    onBackClick()
  }

  const { mutate } = useCounterpartServiceCreateOtherInstitutionEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(
        t('other_institutions.other_institutions_created_success')
      )
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { mutate: edit } = useCounterpartServiceUpdateOtherInstitutionEndpoint({
    onSuccess: () => {
      onMutationSuccess()
      message.success(t('common.save_success'))
    },
    onError: (error: ApiError) => formErrorHandler(error.body.errors)
  })

  const { data, isLoading } = useCounterpartServiceGetOtherInstitutionEndpoint(
    {
      id: String(id)
    },
    [],
    {
      enabled: !!id
    }
  )

  const onBackClick = () => {
    form.resetFields()
    navigate(id ? paths.institutions.details(id) : paths.institutions.root)
  }

  const onSubmit = async () => {
    await form.validateFields()
    const formFields = form.getFieldsValue(true)

    const values = {
      ...formFields,
      status: formFields.status
        ? LegalEntityStatus.ACTIVE
        : LegalEntityStatus.INACTIVE,
      address: {
        ...formFields.address,
        region: undefined,
        regionId: undefined,
        cuatmLocality: undefined,
        cuatmLocalityId:
          data?.address?.cuatmLocality?.translation?.[currentLang?.value] ===
          formFields.address.cuatmLocality
            ? data?.address?.cuatmLocality?.id
            : formFields.address.cuatmLocality
      }
    }

    id
      ? edit({
          id: String(id),
          requestBody: values
        })
      : mutate({
          requestBody: values
        })
  }

  useEffect(() => {
    if (id && data) {
      form.setFieldsValue({
        ...data,
        address: {
          ...data.address,
          region:
            data.address?.cuatmLocality?.parent?.translation?.[
              currentLang?.value
            ],
          regionId: data.address?.cuatmLocality?.parent?.id,
          cuatmLocality:
            data.address?.cuatmLocality?.translation?.[currentLang?.value],
          cuatmLocalityId: data.address?.cuatmLocality?.id
        },
        status: data.status === LegalEntityStatus.ACTIVE
      })
    }
  }, [data, id])

  if (isLoading) {
    return <LoadingScreen />
  }

  return (
    <PageBreadcrumbs
      title={id ? String(data?.name) : t('other_institutions.add_institution')}
      items={[
        {
          title: t('menu.other_institutions'),
          path: paths.counterpart.root
        },
        {
          title: id
            ? String(data?.name)
            : t('other_institutions.add_institution')
        }
      ]}
    >
      <Flex vertical gap={24}>
        <Button
          icon={<LeftOutlined />}
          className="fit-content"
          onClick={onBackClick}
        >
          {t('common.back')}
        </Button>

        <OtherInstitutionsDetails form={form} hasId={!!id} />

        <FormFooter
          onCancel={onBackClick}
          onSave={onSubmit}
          saveLabel={t(id ? 'common.save' : 'common.add')}
          saveConfirmTitle={`${t(id ? 'common.save' : 'common.add')}`}
        />
      </Flex>
    </PageBreadcrumbs>
  )
}
